"use client";
import { useState, useRef, useEffect } from "react";
import { Plus } from "lucide-react";
import Link from "next/link";
import { Sparkles, Heart, Play, ChevronLeft, ChevronRight } from "lucide-react";
import { api, useApp } from "./provider";
import { loadShowcase, type ShowcaseCard } from "@/lib/showcase";
import { ManagedVideo } from "./managed-video";

const requests = new Map<string, Promise<ShowcaseCard[]>>();
function content(query: string, userId: string) {
  const key = `${userId}:${query.trim()}`;
  let pending = requests.get(key);
  if (!pending) {
    pending = loadShowcase(query, api).finally(() => requests.delete(key));
    requests.set(key, pending);
  }
  return pending;
}

export function SampleShowcase({ hero = false, search, onSearchChange }: { hero?: boolean; search?: string; onSearchChange?: (value: string) => void }) {
  const { t, locale, user, languages, error } = useApp();
  const [localSearch, setLocalSearch] = useState('');
  const query = search ?? localSearch;
  const [cards, setCards] = useState<ShowcaseCard[]>([]);
  const [centerIndex, setCenterIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [revision, setRevision] = useState(0);
  const [liking, setLiking] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const touchStart = useRef<number | null>(null);
  useEffect(() => {
    const update = () => setRevision(value => value + 1);
    document.addEventListener("kys-likes-changed", update);
    return () => document.removeEventListener("kys-likes-changed", update);
  }, []);
  useEffect(() => {
    let alive = true;
    setLoading(true); setFailed(false); setCards([]); setCenterIndex(0);
    const timer = setTimeout(() => {
      content(query, user?.id || "guest").then(value => {
        if (alive) { setCards(value); setCenterIndex(Math.floor(value.length / 2)); }
      }).catch(() => { if (alive) setFailed(true); }).finally(() => { if (alive) setLoading(false); });
    }, 200);
    return () => { alive = false; clearTimeout(timer); };
  }, [query, user?.id, revision]);
  async function like(card: ShowcaseCard) {
    if (!card.video || liking) return;
    setLiking(card.id);
    try {
      await api("like", { video_id: card.video.id, liked: !card.video.liked });
      document.dispatchEvent(new Event("kys-likes-changed"));
    } catch (e) { error(e); }
    finally { setLiking(""); }
  }
  function changeSearch(value: string) {
    if (onSearchChange) onSearchChange(value);
    else setLocalSearch(value);
  }
  const moveLeft = () => cards.length && setCenterIndex(prev => (prev - 1 + cards.length) % cards.length);
  const moveRight = () => cards.length && setCenterIndex(prev => (prev + 1) % cards.length);

  const scrollLeft = () => scrollRef.current?.scrollBy({ left: -300, behavior: 'smooth' });
  const scrollRight = () => scrollRef.current?.scrollBy({ left: 300, behavior: 'smooth' });

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };
  
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart.current - touchEnd;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        hero ? moveRight() : scrollRight();
      } else {
        hero ? moveLeft() : scrollLeft();
      }
    }
    touchStart.current = null;
  };

  return (
    <section
      className={hero ? "hero-showcase" : "many-ways container"}
      aria-label={t("Many ways to say it", "多种表达方式")}
    >
      <div className="showcase-heading">
        <span className="eyebrow">
          <Sparkles size={16} />
          {t("MANY WAYS TO SAY IT", "多种表达方式")}
        </span>
        {!hero && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '20px' }}>
            <h2 style={{ margin: 0 }}>
              {t("One word. Different communities.", "一个词，不同的社区。")}
              <br />
              <span>{t("Different ways to sign it.", "不同的手语表达。")}</span>
            </h2>
            
            <div className="showcase-picker" style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '13px', fontWeight: 600, color: 'var(--primary-dark)', gap: '6px' }}>
                {t("Explore a word", "探索词汇")}
                
                <input
                  value={query}
                  placeholder={t("Type a word...", "输入词汇...")}
                  onChange={(e) => changeSearch(e.target.value)}
                  style={{ padding: '8px 36px 8px 16px', borderRadius: '12px', border: '1px solid var(--primary)', background: 'white', color: 'var(--ink)', fontSize: '15px', fontWeight: 500, backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%236d5bde\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Ccircle cx=\'11\' cy=\'11\' r=\'8\'%3E%3C/circle%3E%3Cline x1=\'21\' y1=\'21\' x2=\'16.65\' y2=\'16.65\'%3E%3C/line%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                />

              </label>
            </div>
          </div>
        )}
      </div>

      <div className={hero ? "showcase-carousel-container" : "showcase-horizontal-container"}>
        <button className="carousel-arrow left" disabled={cards.length < 2} onClick={hero ? moveLeft : scrollLeft} aria-label={t("Previous variation", "上一个变体")} >
          <ChevronLeft size={24} />
        </button>
        
        <div 
          className={hero ? "carousel-cards" : "horizontal-cards"} 
          ref={scrollRef}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {cards.length ? cards.map((card, i) => {
            const language = languages.find(value => value.id === card.word.language_id);
            // Color is decorative for supplied previews; it does not claim a language.
            const tone = card.video ? card.word.language_id : ['asl', 'asl-regional', 'sgsl', 'csl', 'community'][i % 5];
            let offset = i - centerIndex;
            const N = cards.length;
            if (N > 1) {
              const half = Math.floor(N / 2);
              if (offset > half) offset -= N;
              if (offset < -half) offset += N;
            }
            return (
              <article 
                key={card.id}
                className={`video-card video-card-${tone}`}
                style={hero ? {
                  transform: `translateX(${offset * 60}px) scale(${1 - Math.abs(offset) * 0.1}) perspective(1000px) rotateY(${offset * -10}deg)`,
                  zIndex: 10 - Math.abs(offset),
                  opacity: Math.abs(offset) > 2 ? 0 : 1,
                  filter: `blur(${Math.abs(offset) * 1.5}px)`,
                  pointerEvents: Math.abs(offset) > 2 ? 'none' : 'auto'
                } : undefined}
                onClick={() => hero && setCenterIndex(i)}
              >
                <div className="video-card-wrap">
                  <ManagedVideo src={card.src} videoId={card.video?.id} poster={card.poster} active={!hero || offset === 0}
                    label={t(`${card.word.en} sign video`, `${card.word.zh}手语视频`)} />
                  <span className="sample-ribbon">
                    <Play size={12} />
                    {card.video ? (card.word.language_id === 'sgsl' ? 'SgSL' : card.word.language_id.toUpperCase()) : t("Supplied preview", "提供的示例")}
                  </span>
                </div>
                <div className="video-card-caption">
                  <div>
                    <h3><Link href={`/word/${encodeURIComponent(card.word.id)}`}>{locale === "en" ? card.word.en : card.word.zh}</Link> <span>{locale === "en" ? card.word.zh : card.word.en}</span></h3>
                    <p>{card.video ? [language ? (locale === "en" ? language.en : language.zh) : card.word.language_id.toUpperCase(), card.video.region, card.video.community].filter(Boolean).join(' · ') : t("Language and community unconfirmed", "语言与社区尚待确认")}</p>
                    {card.video && <p>{card.video.username}</p>}
                  </div>
                  {card.video && <button type="button" className="sample-heart" disabled={!!liking} aria-pressed={card.video.liked}
                    aria-label={t("Like this variation", "为此变体点赞")} onClick={event => { event.stopPropagation(); void like(card); }}>
                    <Heart size={16} fill={card.video.liked ? 'currentColor' : 'none'} />{card.video.likes}
                  </button>}
                </div>
               </article>); }) : (
              <div style={{ textAlign: 'center', background: 'lavender', padding: '40px 20px', borderRadius: '20px', border: '1px solid var(--line)', width: '320px', height: '465px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', boxShadow: 'var(--shadow-sm)', position: 'absolute' }}>
                <h3 role={loading ? "status" : undefined} style={{ fontSize: '18px', marginBottom: '8px', color: 'var(--ink)' }}>{loading ? t("Loading signs…", "正在加载手语…") : failed ? t("We couldn’t load these signs", "暂时无法加载手语") : t("No sign available", "暂无手语")}</h3>
                {!loading && failed && <button className="button" onClick={() => setRevision(value => value + 1)}>{t("Retry", "重试")}</button>}
                {!loading && !failed && <Link href="/upload" className="button primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '20px' }}>
                    <Plus size={16} />
                    {t("Add Your Sign", "添加你的手语")}
                </Link>}
              </div>
            )}
        </div>
          {!!cards.length && (
            <div style={{ position: 'absolute', bottom: hero ? '-30px' : '-50px', left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
               <Link href={`/upload?word=${encodeURIComponent(cards[0].word.id)}`} className="button secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', padding: '6px 16px', borderRadius: '20px', backgroundColor: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(10px)', border: '1px solid var(--line)', boxShadow: 'var(--shadow-sm)' }}>
                  <Plus size={14} />
                  {t("Add Another Sign", "添加其他手语")}
               </Link>
            </div>
          )}
          <button className="carousel-arrow right" disabled={cards.length < 2} onClick={hero ? moveRight : scrollRight} aria-label={t("Next variation", "下一个变体")} >
          <ChevronRight size={24} />
        </button>
      </div>

      
      
      {hero && (
        <a className="stack-foot" href="#many-ways" style={{ display: 'none' }}>
        </a>
      )}
    </section>
  );
}
